import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserRole } from '../helpers/local_storage_helpers';

const Drive = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Mock employee data - replace with API call
  const mockEmployees = [
    { 
      id: 1, 
      name: 'John Smith', 
      role: 'Sales Rep', 
      email: 'john@company.com',
      sheetsCount: 5,
      lastActive: '2024-01-15',
      status: 'active'
    },
    { 
      id: 2, 
      name: 'Sarah Johnson', 
      role: 'Marketing Manager', 
      email: 'sarah@company.com',
      sheetsCount: 8,
      lastActive: '2024-01-14',
      status: 'active'
    },
    { 
      id: 3, 
      name: 'Mike Davis', 
      role: 'Sales Rep', 
      email: 'mike@company.com',
      sheetsCount: 3,
      lastActive: '2024-01-13',
      status: 'inactive'
    },
    { 
      id: 4, 
      name: 'Lisa Wilson', 
      role: 'Team Lead', 
      email: 'lisa@company.com',
      sheetsCount: 12,
      lastActive: '2024-01-15',
      status: 'active'
    },
    { 
      id: 5, 
      name: 'Tom Brown', 
      role: 'Sales Rep', 
      email: 'tom@company.com',
      sheetsCount: 6,
      lastActive: '2024-01-12',
      status: 'active'
    },
    { 
      id: 6, 
      name: 'Emma Taylor', 
      role: 'Marketing Specialist', 
      email: 'emma@company.com',
      sheetsCount: 4,
      lastActive: '2024-01-11',
      status: 'active'
    },
  ];

  useEffect(() => {
    // Simulate API call
    setIsLoading(true);
    setTimeout(() => {
      setEmployees(mockEmployees);
      setIsLoading(false);
    }, 500);
  }, []);

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading employees...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-800 mb-1">Drive</h1>
        <p className="text-sm text-gray-600">Manage employee spreadsheets and assignments</p>
      </div>

      {/* Search and Filters */}
      <div className="mb-4">
        <div className="flex gap-3 items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <select className="px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="">All Roles</option>
              <option value="Sales Rep">Sales Rep</option>
              <option value="Marketing Manager">Marketing Manager</option>
              <option value="Team Lead">Team Lead</option>
              <option value="Marketing Specialist">Marketing Specialist</option>
            </select>
            <select className="px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-blue-600">{employees.length}</div>
          <div className="text-xs text-gray-600">Total Employees</div>
        </div>
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-green-600">
            {employees.filter(emp => emp.status === 'active').length}
          </div>
          <div className="text-xs text-gray-600">Active</div>
        </div>
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-purple-600">
            {employees.reduce((sum, emp) => sum + emp.sheetsCount, 0)}
          </div>
          <div className="text-xs text-gray-600">Total Sheets</div>
        </div>
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-orange-600">
            {Math.round(employees.reduce((sum, emp) => sum + emp.sheetsCount, 0) / employees.length)}
          </div>
          <div className="text-xs text-gray-600">Avg Sheets/Employee</div>
        </div>
      </div>

      {/* Employee Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredEmployees.map((employee) => (
          <div
            key={employee.id}
            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-3"
            onClick={() => navigate(`/drive/${employee.id}`)}
          >
            <div className="text-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-blue-600 font-semibold text-sm">
                  {employee.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <h3 className="text-sm font-medium text-gray-800 truncate">{employee.name}</h3>
              <p className="text-xs text-gray-500 truncate">{employee.role}</p>
              <div className="flex items-center justify-center mt-1">
                <span className={`w-2 h-2 rounded-full ${employee.status === 'active' ? 'bg-green-400' : 'bg-gray-400'}`}></span>
                <span className="text-xs text-gray-500 ml-1">{employee.sheetsCount}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No employees found</div>
          <div className="text-gray-400 text-sm">Try adjusting your search criteria</div>
        </div>
      )}
    </div>
  );
};

export default Drive;


